/* eslint-disable react/forbid-prop-types */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '../../../../components/Grid/Grid';
import Button from '../../../../components/Button/Button';
import InputField from '../../../../components/InputField/InputField';
import Dialog from '../../../../components/Dialog/Dialog';
import DialogTitle from '../../../../components/DialogTitle/DialogTitle';
import useForm from '../../../../hooks/useForm';
import DialogContent from '../../../../components/DialogContent/DialogContent';
import {
  addPowderBatchTemperatureConfig,
  getPowderBatchTemperatureConfigList,
} from '../../../../services/settings-config';
import useCancelToken from '../../../../hooks/useCancelToken';
import CircularProgress from '../../../../components/CircularProgress/CircularProgress';
import Typography from '../../../../components/Typography/Typography';
import useStyles from '../../useStyles';
import Snackbar from '../../../../components/Snackbar/Snackbar';
import PowderBatchTemperatureConfigRow from './PowderBatchTemperatureConfigRow';

const PowderBatchTemperatureUpdateDialog = ({ isOpen, onClose, targetRow }) => {
  const { classes } = useStyles();
  const { newCancelToken } = useCancelToken();
  const initialForm = {
    diameter: '',
    temperature: '',
  };
  const { formData, setFormData, formErr, setFormErr, resetFormErr } = useForm(initialForm);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false); // 用於標示是否有Row正在編輯或刪除中，以防止同時操作多個Row
  const [temperatureConfigList, setTemperatureConfigList] = useState([]);
  const [temperatureConfigLoading, setTemperatureConfigLoading] = useState(false);
  const [temperatureConfigError, setTemperatureConfigError] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [statusType, setStatusType] = useState('success');
  const [showStatus, setShowStatus] = useState(false);
  const snackbarTimer = useRef(null);

  const openSnackbar = () => {
    setShowStatus(true);
    clearTimeout(snackbarTimer.current);
    snackbarTimer.current = setTimeout(() => {
      setShowStatus(false);
      clearTimeout(snackbarTimer.current);
    }, 3000);
  };

  const validationForm = () => {
    if (
      formData.diameter.length === 0 ||
      Number.isNaN(Number(formData.diameter)) ||
      Number(formData.diameter) <= 0
    ) {
      setFormErr({ ...formErr, diameter: '請輸入正確尺寸' });
      return false;
    }
    if (
      formData.temperature.length === 0 ||
      Number.isNaN(Number(formData.temperature)) ||
      Number(formData.temperature) <= 0
    ) {
      setFormErr({ ...formErr, temperature: '請輸入正確溫度' });
      return false;
    }
    return true;
  };

  const handleClose = () => {
    resetFormErr();
    setFormData(initialForm);
    setTemperatureConfigList([]);
    setTemperatureConfigError('');
    setTemperatureConfigLoading(false);
    onClose();
  };

  const handleGetTemperatureConfigList = async () => {
    const cancelToken = newCancelToken();
    setTemperatureConfigLoading(true);
    setTemperatureConfigError('');
    await getPowderBatchTemperatureConfigList({
      data: {
        deviceId: Number(targetRow.powderBatch),
      },
      cancelToken,
      onSuccess: (result) => {
        setTemperatureConfigList(result.configList);
      },
      onError: (error) => {
        setTemperatureConfigError(error);
      },
      onSettled: () => {
        setTemperatureConfigLoading(false);
      },
    });
  };

  const handleAddTemperatureConfig = () => {
    setIsCreateMode(true);
  };

  const handleSubmitAddTemperatureConfig = async () => {
    if (!validationForm()) return;
    const cancelToken = newCancelToken();
    setIsSubmitting(true);
    await addPowderBatchTemperatureConfig({
      data: {
        deviceId: Number(targetRow.powderBatch),
        size: Number(formData.diameter),
        temperature: Number(formData.temperature),
      },
      cancelToken,
      onSuccess: () => {
        setIsCreateMode(false);
        openSnackbar();
        setStatusType('success');
        setStatusMsg('新增溫度設定成功！');
        resetFormErr();
        setFormData(initialForm);
        handleGetTemperatureConfigList();
      },
      onError: (result, _rtCode, noPrivilegeMsg) => {
        openSnackbar();
        setStatusType('error');
        setStatusMsg(
          noPrivilegeMsg || `新增溫度設定失敗！請確認網路是否正常或聯繫軟體部門：${result}`
        );
      },
      onSettled: () => {
        setIsSubmitting(false);
      },
    });
  };

  const handleEditTemperatureConfig = () => {
    setIsEditMode(true);
  };

  const handleCancelEditTemperatureConfig = () => {
    setIsEditMode(false);
  };

  const handleSaveTemperatureConfig = () => {
    setIsEditMode(false);
    openSnackbar();
    setStatusType('success');
    setStatusMsg('儲存溫度設定成功！');
    handleGetTemperatureConfigList();
  };

  const handleSaveTemperatureConfigError = (result, noPrivilegeMsg) => {
    openSnackbar();
    setStatusType('error');
    setStatusMsg(noPrivilegeMsg || `儲存溫度表失敗！請確認網路是否正常或聯繫軟體部門：${result}`);
    setIsEditMode(false);
  };

  const handleDeleteTemperatureConfigClick = () => {
    setIsEditMode(true);
  };

  const handleCancelAddTemperatureConfig = () => {
    setIsCreateMode(false);
    setFormData(initialForm);
  };

  const handleDeleteTemperatureConfig = () => {
    setIsEditMode(false);
    openSnackbar();
    setStatusType('success');
    setStatusMsg('刪除溫度設定成功！');
    handleGetTemperatureConfigList();
  };

  const handleDeleteTemperatureConfigError = (result, noPrivilegeMsg) => {
    setIsEditMode(false);
    openSnackbar();
    setStatusType('error');
    setStatusMsg(noPrivilegeMsg || `刪除溫度設定失敗！請確認網路是否正常或聯繫軟體部門：${result}`);
  };

  useEffect(() => {
    if (isOpen) {
      handleGetTemperatureConfigList();
    }
  }, [isOpen]);

  return (
    <Dialog
      open={isOpen}
      onClose={(_e, reason) => {
        if (reason && reason === 'backdropClick') return;
        handleClose();
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id="update-powder-batch-temperature-title">編輯試燒環溫度表</DialogTitle>
      <DialogContent dividers sx={{ paddingTop: 3, paddingBottom: 3 }}>
        <Grid container direction="column" spacing={2}>
          <Grid container spacing={2} alignItems="cetner">
            <Grid item xs={3}>
              <InputField
                id="powderBatch"
                label="試燒環編號"
                value={targetRow.powderBatch}
                variant="outlined"
                disabled
              />
            </Grid>
          </Grid>
          <Grid
            item
            container
            direction="column"
            xs={12}
            sx={
              temperatureConfigLoading || temperatureConfigError || temperatureConfigList.length > 0
                ? { paddingTop: 3, paddingBottom: 3 }
                : {}
            }
          >
            <Snackbar
              msg={statusMsg}
              type={statusType}
              open={showStatus}
              className={classes.snackbar}
            />
            {temperatureConfigLoading && <CircularProgress />}
            {!temperatureConfigLoading && temperatureConfigError && (
              <Typography variant="body1" color="error" textAlign="center">
                {temperatureConfigError}
              </Typography>
            )}
            {temperatureConfigList.length > 0 && (
              <Grid
                container
                item
                xs={12}
                spacing={2}
                direction="column"
                sx={{
                  paddingLeft: 2,
                  paddingRight: 2,
                  overflowY: 'scroll',
                  height: '400px',
                  flexWrap: 'nowrap',
                }}
              >
                <Grid container item xs={12} justifyContent="flex-start" sx={{ paddingBottom: 3 }}>
                  {isCreateMode ? (
                    <>
                      <Grid item xs={3}>
                        <InputField
                          id="diameter"
                          size="small"
                          label="Diameter (mm)"
                          placeholder="請輸入尺寸"
                          type="number"
                          value={formData.diameter}
                          variant="outlined"
                          onChange={(e) => setFormData({ ...formData, diameter: e.target.value })}
                          error={formErr.diameter}
                          errormsg={formErr.diameter}
                          required
                        />
                      </Grid>
                      <Grid item xs={7}>
                        <InputField
                          id="temperature"
                          size="small"
                          label="Temperature (°C)"
                          placeholder="請輸入溫度"
                          type="number"
                          value={formData.temperature}
                          variant="outlined"
                          onChange={(e) =>
                            setFormData({ ...formData, temperature: e.target.value })
                          }
                          error={formErr.temperature}
                          errormsg={formErr.temperature}
                          required
                        />
                      </Grid>
                      <Grid
                        item
                        container
                        xs={2}
                        alignItems="flex-start"
                        gap={1}
                        sx={{ marginTop: 1.2 }}
                      >
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={handleSubmitAddTemperatureConfig}
                          size="small"
                          sx={{ backgroundColor: 'white', height: 32 }}
                          disabled={isSubmitting}
                        >
                          <Typography variant="body1">新增</Typography>
                        </Button>
                        <Button
                          variant="outlined"
                          color="grey"
                          onClick={handleCancelAddTemperatureConfig}
                          size="small"
                          sx={{ backgroundColor: 'white', height: 32 }}
                          disabled={isSubmitting}
                        >
                          <Typography variant="body1">取消</Typography>
                        </Button>
                      </Grid>
                    </>
                  ) : (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleAddTemperatureConfig}
                      sx={{ backgroundColor: 'white' }}
                    >
                      新增溫度設定
                    </Button>
                  )}
                </Grid>
                <Grid
                  container
                  item
                  xs={12}
                  sx={(theme) => ({
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    padding: 0,
                    marginBottom: 0.6,
                  })}
                >
                  <Grid item xs={3}>
                    <Typography variant="body1">Diameter (mm)</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body1">Temperature (°C)</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body1" textAlign="right">
                      Action
                    </Typography>
                  </Grid>
                </Grid>
                {temperatureConfigList.map((item) => (
                  <PowderBatchTemperatureConfigRow
                    key={`${item.size}-${item.temperature}`}
                    configRow={item}
                    onSaveTemperatureConfig={handleSaveTemperatureConfig}
                    onSaveTemperatureConfigError={handleSaveTemperatureConfigError}
                    onDeleteTemperatureConfigClick={handleDeleteTemperatureConfigClick}
                    onDeleteTemperatureConfig={handleDeleteTemperatureConfig}
                    onDeleteTemperatureConfigError={handleDeleteTemperatureConfigError}
                    onEditTemperatureConfig={handleEditTemperatureConfig}
                    onCancelEditTemperatureConfig={handleCancelEditTemperatureConfig}
                    isUnderEditMode={isEditMode}
                    powderBatch={targetRow.powderBatch?.toString()}
                  />
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      {temperatureConfigList.length > 0 && (
        <Grid container item xs={12} justifyContent="flex-end" sx={{ padding: 2 }}>
          <Button variant="outlined" color="grey" onClick={handleClose}>
            回上一頁
          </Button>
        </Grid>
      )}
    </Dialog>
  );
}; // NewQCTypeAction
PowderBatchTemperatureUpdateDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  targetRow: PropTypes.object.isRequired,
};

export default PowderBatchTemperatureUpdateDialog;
